/* Statuswijzigingen van een Offerte (ADR 0018): verstuurd, geaccepteerd, afgewezen,
 * en terug. De client komt als argument binnen, zodat de toetsen hem kunnen nabootsen.
 *
 * "Markeer als verstuurd" legt dag, ontvanger en revisie vast en maakt een
 * herinnertaak in unified_todos, HERINNER_NA_DAGEN na de verzenddag, bij het traject
 * van de offerte (project, Kavel of Opportunity). Een besluit rondt die taak af;
 * opnieuw versturen vervangt hem. */

import { HERINNER_NA_DAGEN, plusDagen } from './status';

export interface OfferteClient {
  from(tabel: string): any;
  rpc(fn: string, args?: Record<string, unknown>): any;
  auth: { getUser(): Promise<{ data: { user: { id: string } | null } }> };
}

/** Wat de acties van een offerte nodig hebben. */
export interface OfferteKern {
  id: string;
  quote_number: string;
  title: string;
  status: string;
  current_version: number | null;
  project_id: string | null;
  charging_location_id: string | null;
  charging_opportunity_id: string | null;
  sent_on?: string | null;
  follow_up_todo_id?: string | null;
}

export class OfferteFout extends Error {
  override name = 'OfferteFout';
}

const DAG_RE = /^\d{4}-\d{2}-\d{2}$/;
const TAAK_OPEN = ['open', 'in_progress', 'blocked'];
const DATUM = new Intl.DateTimeFormat('nl-NL', { day: 'numeric', month: 'long', year: 'numeric', timeZone: 'UTC' });
const datumNl = (dag: string) => DATUM.format(new Date(`${dag}T00:00:00Z`));

async function gebruiker(client: OfferteClient): Promise<string> {
  const { data } = await client.auth.getUser();
  if (!data.user) throw new OfferteFout('Niet ingelogd.');
  return data.user.id;
}

function controleerDag(dag: string, wat: string): void {
  if (!DAG_RE.test(dag)) throw new OfferteFout(`${wat} is geen geldige datum.`);
}

/** Bij welk traject de taak hoort: dezelfde eigenaar als de offerte (ADR 0001, losse FK's). */
export function taakEigenaar(q: Pick<OfferteKern, 'project_id' | 'charging_location_id' | 'charging_opportunity_id'>) {
  if (q.project_id) return { project_id: q.project_id, scope: 'team_crm', is_shared: true };
  if (q.charging_location_id) return { location_id: q.charging_location_id, scope: 'location', is_shared: true };
  if (q.charging_opportunity_id) return { opportunity_id: q.charging_opportunity_id, scope: 'opportunity', is_shared: true };
  return { scope: 'personal_crm', is_shared: false };
}

export function herinnerTaak(q: OfferteKern, v: { op: string; aan: string; revisie: number | null }, userId: string) {
  return {
    title: `Offerte ${q.quote_number} opvolgen`,
    description: [
      `Offerte ${q.quote_number} (${q.title}) is op ${datumNl(v.op)} verstuurd aan ${v.aan}${v.revisie ? `, revisie ${v.revisie}` : ''}.`,
      'Nog geen reactie? Neem contact op en leg het besluit vast op de offerte.',
      `/crm/quotes/${q.id}`,
    ].join('\n'),
    due_date: plusDagen(v.op, HERINNER_NA_DAGEN),
    assigned_to: userId,
    created_by: userId,
    priority: 'medium',
    status: 'open',
    ...taakEigenaar(q),
  };
}

async function sluitTaak(client: OfferteClient, id: string | null | undefined, status: 'completed' | 'cancelled'): Promise<void> {
  if (!id) return;
  const wijziging = status === 'completed' ? { status, completed_at: new Date().toISOString() } : { status };
  // Alleen een taak die nog open staat: wie hem al afvinkte of weggooide, houdt dat.
  const { error } = await client.from('unified_todos').update(wijziging).eq('id', id).in('status', TAAK_OPEN);
  if (error) throw error;
}

export interface Verstuurd {
  /** Dag van versturen, YYYY-MM-DD. */
  op: string;
  /** Ontvanger: naam en/of e-mailadres. */
  aan: string;
  revisie: number | null;
}

/** Status verstuurd, met dag, ontvanger en revisie, plus een herinnertaak. Geeft het id van de taak. */
export async function markeerVerstuurd(client: OfferteClient, q: OfferteKern, v: Verstuurd): Promise<string> {
  controleerDag(v.op, 'De verzenddatum');
  const aan = v.aan.trim();
  if (aan.length < 2) throw new OfferteFout('Vul in aan wie de offerte verstuurd is.');
  const userId = await gebruiker(client);

  const { data: taak, error: taakFout } = await client
    .from('unified_todos')
    .insert(herinnerTaak(q, { ...v, aan }, userId))
    .select('id')
    .single();
  if (taakFout) throw taakFout;

  const { error } = await client
    .from('quotes')
    .update({
      status: 'sent',
      sent_on: v.op,
      sent_to: aan,
      sent_revision: v.revisie,
      decided_on: null,
      decision_note: null,
      follow_up_todo_id: taak.id,
    })
    .eq('id', q.id);
  if (error) {
    // De offerte bleef zoals hij was; de net gemaakte taak hoort dan niet te blijven staan.
    await sluitTaak(client, taak.id, 'cancelled').catch(() => {});
    throw error;
  }
  // Opnieuw verstuurd (een nieuwe revisie): de oude herinnering is achterhaald.
  if (q.follow_up_todo_id && q.follow_up_todo_id !== taak.id) await sluitTaak(client, q.follow_up_todo_id, 'cancelled');
  return taak.id;
}

export interface Besluit {
  uitkomst: 'accepted' | 'declined';
  op: string;
  notitie?: string | null;
}

/** Geaccepteerd of afgewezen, met de dag van het besluit; de herinnertaak is dan klaar. */
export async function legBesluitVast(client: OfferteClient, q: OfferteKern, b: Besluit): Promise<void> {
  controleerDag(b.op, 'De datum van het besluit');
  if (b.uitkomst !== 'accepted' && b.uitkomst !== 'declined') throw new OfferteFout('Onbekende uitkomst.');
  const notitie = b.notitie?.trim() || null;
  const { error } = await client
    .from('quotes')
    .update({ status: b.uitkomst, decided_on: b.op, decision_note: notitie })
    .eq('id', q.id);
  if (error) throw error;
  await sluitTaak(client, q.follow_up_todo_id, 'completed');
}

/**
 * Eén stap terug. Een besluit wordt weer "verstuurd" (of concept als de offerte nooit
 * verstuurd is); verstuurd wordt concept, en de herinnering vervalt.
 */
export async function zetStatusTerug(client: OfferteClient, q: OfferteKern): Promise<'draft' | 'sent'> {
  if (q.status === 'accepted' || q.status === 'declined') {
    const naar = q.sent_on ? 'sent' : 'draft';
    const { error } = await client.from('quotes').update({ status: naar, decided_on: null, decision_note: null }).eq('id', q.id);
    if (error) throw error;
    return naar;
  }
  if (q.status === 'sent') {
    const { error } = await client
      .from('quotes')
      .update({ status: 'draft', sent_on: null, sent_to: null, sent_revision: null, follow_up_todo_id: null })
      .eq('id', q.id);
    if (error) throw error;
    await sluitTaak(client, q.follow_up_todo_id, 'cancelled');
    return 'draft';
  }
  return 'draft';
}
